import { DoneButton } from 'components/blue-button'
import EditActionRow from 'components/lesson-builder/components/edit-action-row'
import React, { useState, useEffect } from 'react'
import 'css/components/lesson-builder/question-types.css'
import { Question } from 'types'
import type { MultipleChoiceQuestion } from 'types'
import imageIcon from 'img/image.svg'
import AddImageOverlay from 'components/lesson-builder/components/add-image-overlay'
import styles from 'css/components/lesson-builder/multiple-choice.module.css'

// Type guard for multiple choice questions
const isMultipleChoiceQuestion = (question: Question): question is MultipleChoiceQuestion => {
  return question.type === 'multiple-choice'
}

/**
 * Multiple Choice View Component - displays the question, options, and correctanswer
 * @param {Object} props
 * @param {string} props.question
 * @param {string[]} props.options
 * @param {number} props.answer
 */
export const MultipleChoiceViewer = ({
  question,
  onRemoveImage,
}: { question: Question, onRemoveImage?: (optionIndex?: number) => void }) => {
  // Type guard to ensure this is a multiple choice question
  if (!isMultipleChoiceQuestion(question)) {
    return <div>Invalid question type for multiple choice viewer</div>
  }

  return (
    <div className="question-type multiple-choice">
      {question.imageUrl && (
        <div className="image-remove-wrapper">
          <img className="question-image" src={question.imageUrl} alt="multiple choice" />
          {onRemoveImage && <button className="remove-image-btn" onClick={() => onRemoveImage()} title="Remove image">✕</button>}
        </div>
      )}
      <p className="question-text">
        {Array.isArray(question.question) ? question.question.join(' ') : question.question}
      </p>
      <div className="options-container">
        {question.options.map((option, index) => (
          <>
            <div
              key={index}
              className={`option ${question.answer === index ? 'selected' : ''}`}
            >

              <span className="option-letter">{String.fromCharCode(65 + index)}.</span>
              <span className="option-text">{option}</span>
            </div>
            {isMultipleChoiceQuestion(question) && question.optionImageUrls?.[index] && (
              <div className="image-remove-wrapper">
                <img className={styles.optionImage} src={question.optionImageUrls[index]} alt="multiple choice" />
                {onRemoveImage && <button className="remove-image-btn" onClick={() => onRemoveImage(index)} title="Remove image">✕</button>}
              </div>
            )}
          </>
        ))}
      </div>
    </div>
  )
} 

interface MultipleChoiceAnsweringProps {
  question: Question
  answer?: string
  onAnswerChange?: (answer: string) => void
}

export const MultipleChoiceAnswering = ({ question, answer, onAnswerChange }: MultipleChoiceAnsweringProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const isMC = isMultipleChoiceQuestion(question)

  // Sync selected index from provided answer text (must run before any conditional return)
  useEffect(() => {
    if (!isMC) {
      setSelectedIndex(null)
      return
    }
    if (!answer) {
      setSelectedIndex(null)
      return
    }
    const idx = question.options.indexOf(answer)
    setSelectedIndex(idx >= 0 ? idx : null)
  }, [answer, isMC, question])

  // Type guard to ensure this is a multiple choice question
  if (!isMC) {
    return <div>Invalid question type for multiple choice</div>
  }


  const handleSelect = (index: number) => {
    setSelectedIndex(index)
    if (onAnswerChange) onAnswerChange(question.options[index])
  }

  return (
    <div className="question-type multiple-choice">
      {question.imageUrl && <img className="question-image" src={question.imageUrl} alt="multiple choice" />}
      <p className="question-text">
        {Array.isArray(question.question) ? question.question.join(' ') : question.question}
      </p>
      <div className="options-container">
        {question.options.map((option, index) => (
          <>
            <div
              key={index}
              className={`option ${selectedIndex === index ? 'selected' : ''}`}
              onClick={() => handleSelect(index)}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}.</span>
              <span className="option-text">{option}</span>
            </div>
            {isMultipleChoiceQuestion(question) && question.optionImageUrls?.[index] && (
              <img className={styles.optionImage} src={question.optionImageUrls[index]} alt="multiple choice" />
            )}
          </>
        ))}
      </div>
      {/* Done button is handled by parent (student-live) */}
    </div>
  )
}

interface MultipleChoiceEditorProps {
  question: Question
  onEdit: (payload: Partial<Question>) => void
  setIsEditing: (editing: boolean) => void
  setShowFeedback?: (showFeedback: boolean) => void
}

export const MultipleChoiceEditor = ({ question, onEdit, setIsEditing, setShowFeedback }: MultipleChoiceEditorProps) => {
  // Initialize state with default values
  const [questionText, setQuestionText] = useState('')
  const [optionsArray, setOptionsArray] = useState<string[]>([])
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  // Centralized image overlay state
  const [showImageOverlay, setShowImageOverlay] = useState(false)
  const [imageTarget, setImageTarget] = useState<null | { type: 'question' } | { type: 'option', index: number }>(null)

  // Update state with actual question data if this is a valid multiple choice question
  useEffect(() => {
    if (isMultipleChoiceQuestion(question)) {
      setQuestionText(question.question)
      setOptionsArray(question.options)
      setSelectedAnswer(question.answer)
    }
  }, [question])

  // Type guard to ensure this is a multiple choice question
  if (!isMultipleChoiceQuestion(question)) {
    return <div>Invalid question type for multiple choice editor</div>
  }

  const handleOptionChange = (index, value) => {
    const newOptions = [...optionsArray]
    newOptions[index] = value
    setOptionsArray(newOptions)
  }

  const handleDone = () => {
    onEdit({
      question: questionText,
      options: optionsArray,
      answer: selectedAnswer
    })
    setIsEditing(false)
  }

  const openQuestionImageOverlay = () => {
    setImageTarget({ type: 'question' })
    setShowImageOverlay(true)
  }

  const openOptionImageOverlay = (index: number) => {
    setImageTarget({ type: 'option', index })
    setShowImageOverlay(true)
  }

  const handleUploadImage = (url: string) => {
    if (!imageTarget) return
    if (imageTarget.type === 'question') {
      onEdit({ imageUrl: url })
    } else {
      const currentRecord: Record<number, string> = isMultipleChoiceQuestion(question) && question.optionImageUrls ? { ...question.optionImageUrls } : {}
      currentRecord[imageTarget.index] = url
      onEdit({ optionImageUrls: currentRecord })
    }
    // keep overlay open; user can close manually via Done
  }

  return (
    <div className="question-type multiple-choice">
      {question.imageUrl && <img className="question-image" src={question.imageUrl} alt="multiple choice" />}
      Question:
      <div className="question-text">
        <input
          className="editor-input"
          type="text"
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          placeholder="Enter your question here"
        />
      </div>
      {setShowFeedback && (
        <EditActionRow setShowFeedback={setShowFeedback} onEdit={onEdit} onAddImage={openQuestionImageOverlay} />
      )}
      <div className="options-container">
        Options:
        {optionsArray.map((option, index) => (
          <>
              <input
                className="editor-input"
                type="text"
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                placeholder={`Option ${index + 1}`}
              />
              { question.optionImageUrls?.[index] && <img className={styles.optionImage} src={question.optionImageUrls[index]} alt="multiple choice" /> }
              <button className="editor-action-button alignRight" onClick={() => openOptionImageOverlay(index)}>
                <img src={imageIcon} alt="add image" />
                Add Image
              </button>
            </>
        ))}
      </div>
      {showImageOverlay && (
        <AddImageOverlay
          setShowAddImageOverlay={setShowImageOverlay}
          onUpload={handleUploadImage}
          title="Add Image"
          note={imageTarget?.type === 'option' ? 'Note: Only one image can be inserted below each answer.' : 'Note: Only one image can be inserted before this question.'}
          optionText={imageTarget?.type === 'option' ? optionsArray[imageTarget.index] : questionText}
        />
      )}
      <DoneButton onClick={handleDone} />
    </div>
  )
}